import { z } from 'zod';

// ── Round ─────────────────────────────────────────────────────────────────

export const SubmittedWordDto = z.object({
  word: z.string(),
  rawScore: z.number(),
  submittedAt: z.string(),
});
export type SubmittedWordDto = z.infer<typeof SubmittedWordDto>;

export const RejectedWordDto = z.object({
  word: z.string(),
  reason: z.string(),
  rejectedAt: z.string(),
});
export type RejectedWordDto = z.infer<typeof RejectedWordDto>;

export const RoundDto = z.object({
  roundId: z.string(),
  roomCode: z.string(),
  matchId: z.string().nullable().optional(),
  roundIndex: z.number(),
  totalRounds: z.number(),
  letters: z.string(),
  status: z.enum(['InProgress', 'Ended']),
  startedAtUtc: z.string(),
  endsAtUtc: z.string(),
  serverNowUtc: z.string(),
  myWords: z.array(SubmittedWordDto),
  myRejections: z.array(RejectedWordDto),
});
export type RoundDto = z.infer<typeof RoundDto>;

export const WordResultDto = z.discriminatedUnion('accepted', [
  z.object({
    accepted: z.literal(true),
    word: z.string(),
    rawScore: z.number(),
    isUnique: z.boolean().optional().default(false),
  }),
  z.object({
    accepted: z.literal(false),
    word: z.string(),
    reason: z.string(),
  }),
]);
export type WordResultDto = z.infer<typeof WordResultDto>;

export const ProgressPlayerDto = z.object({
  playerId: z.union([z.string(), z.number()]).transform(String),
  nickname: z.string(),
  wordCount: z.number().optional(),
  totalScore: z.number().optional(),
  isConnected: z.boolean(),
});

export const ProgressDto = z.object({
  active: z.boolean(),
  visibility: z.string().optional(),
  players: z.array(ProgressPlayerDto).optional(),
});
export type ProgressDto = z.infer<typeof ProgressDto>;

export const RoundResultWordDto = z.object({
  word: z.string(),
  rawScore: z.number(),
  isUnique: z.boolean(),
  speedBonus: z.number(),
  finalScore: z.number(),
});

export const RoundResultPlayerDto = z.object({
  playerId: z.union([z.string(), z.number()]).transform(String),
  nickname: z.string(),
  totalScore: z.number(),
  words: z.array(RoundResultWordDto),
});

export const RoundResultsDto = z.object({
  roundFinished: z.boolean(),
  roundId: z.string().optional(),
  letters: z.string().optional(),
  players: z.array(RoundResultPlayerDto).optional(),
});
export type RoundResultsDto = z.infer<typeof RoundResultsDto>;

// ── Match ─────────────────────────────────────────────────────────────────

export const MatchStandingDto = z.object({
  playerId: z.union([z.string(), z.number()]).transform(String),
  nickname: z.string(),
  roundScores: z.array(z.number()),
  totalScore: z.number(),
});

export const MatchDto = z.object({
  matchId: z.string(),
  status: z.enum(['InProgress', 'BetweenRounds', 'Finished']),
  currentRoundIndex: z.number(),
  totalRounds: z.number(),
  nextRoundStartsAtUtc: z.string().nullable().optional(),
  standings: z.array(MatchStandingDto),
});
export type MatchDto = z.infer<typeof MatchDto>;

export const PlayerDto = z.object({
  playerId: z.string(),
  nickname: z.string(),
  isReady: z.boolean(),
  isConnected: z.boolean(),
});
export type PlayerDto = z.infer<typeof PlayerDto>;

export const RoomSettingsDto = z.object({
  mode: z.enum(['SingleRound', 'Match', 'Solo']),
  roundDurationSec: z.number().int(),
  totalRounds: z.number().int(),
  speedBonus: z.boolean(),
  progressVisibility: z.enum(['WordCount', 'Score', 'Hidden']),
  isPublic: z.boolean(),
});
export type RoomSettingsDto = z.infer<typeof RoomSettingsDto>;

export const RoomDto = z.object({
  code: z.string(),
  status: z.enum(['WaitingForPlayers', 'InGame', 'BetweenRounds', 'Finished', 'Archived']),
  settings: RoomSettingsDto,
  hostPlayerId: z.string().nullable().optional(),
  players: z.array(PlayerDto),
  maxPlayers: z.number().int(),
  createdAt: z.string(),
  // Present only on create/join responses for the calling account (SEC-A/02); absent
  // elsewhere (e.g. GET /rooms/{code}) so a room code alone can't leak someone's token.
  sessionToken: z.string().nullable().optional(),
});
export type RoomDto = z.infer<typeof RoomDto>;

export const GuestRegistrationResponse = z.object({
  playerId: z.string(),
  nickname: z.string(),
  sessionToken: z.string(),
});
export type GuestRegistrationResponse = z.infer<typeof GuestRegistrationResponse>;

// ── PlayerProfile ─────────────────────────────────────────────────────────

export const PlayerStatsDto = z.object({
  games: z.number(),
  winrate: z.number(),
  avgScore: z.number(),
  words: z.number(),
  best: z.object({ word: z.string(), score: z.number() }).nullable(),
});
export type PlayerStatsDto = z.infer<typeof PlayerStatsDto>;

export const PlayerHistoryEntryDto = z.object({
  letters: z.tuple([z.string(), z.string(), z.string()]),
  mode: z.string(),
  place: z.number(),
  players: z.number(),
  score: z.number(),
  when: z.string(),
});
export type PlayerHistoryEntryDto = z.infer<typeof PlayerHistoryEntryDto>;

export const PlayerProfileDto = z.object({
  stats: PlayerStatsDto,
  history: z.array(PlayerHistoryEntryDto),
});
export type PlayerProfileDto = z.infer<typeof PlayerProfileDto>;
